/**
 * Compact LogoUpload — small inline image-upload control for inside
 * narrow style panels (Header/Footer editor cards). Wraps the existing
 * /api/media/upload endpoint used elsewhere.
 */
import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { SafeImg } from './SafeImg';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function LogoUploadInput({ value, onChange, label = 'Logo', testId, hint }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const token = localStorage.getItem('token');

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/media/upload?category=branding`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      const url = process.env.REACT_APP_BACKEND_URL + res.data.url;
      onChange(url);
      toast.success(`${label} uploaded`);
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid={`${testId}-wrapper`}>
      <p className="text-[12px] text-[#9CA3AF] mb-2">{label}{hint && <span className="ml-2 text-[10px] text-[#64748B]">{hint}</span>}</p>
      <div className="flex items-center gap-2">
        {value ? (
          <div className="relative group">
            <SafeImg src={value} alt={label} className="w-14 h-14 rounded-md object-contain bg-white/[0.06] border border-white/[0.1] p-1" />
            <button
              type="button"
              onClick={() => onChange('')}
              className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500/80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`${testId}-clear`}
              aria-label="Clear logo"
            >
              <X size={9} className="text-white" />
            </button>
          </div>
        ) : (
          <div className="w-14 h-14 rounded-md bg-white/[0.04] border border-dashed border-white/[0.1] flex items-center justify-center text-[10px] text-[#64748B]">
            none
          </div>
        )}
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-1.5 px-3 py-2 rounded-md text-[11px] font-bold uppercase tracking-wider bg-white/[0.06] border border-white/[0.1] text-white hover:border-white/[0.2] disabled:opacity-50"
          data-testid={testId}
        >
          {uploading ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
          {uploading ? 'Uploading' : value ? 'Replace' : 'Upload'}
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(e.target.files?.[0])} />
      </div>
    </div>
  );
}

export default LogoUploadInput;
